import moment from 'moment';
import { useState } from 'react';
import { get, post, put, remove as rm } from '../../redux/api';
import { dropAt } from '../../utils';
import { useAsyncStatus } from '../index';
import useRequestEventEmitter from './useRequestEventEmitter';
import { makeRequest, Method, RequestState } from './makeRequest';
import type { Banner, CreateBannerRequest, DeactivateBannerRequest, GetBannersResponse, UpdateBannerRequest } from '../../../typings/api';
import type { AsyncStatus } from '../../types';
import type { DeleteRequestEventContext, GetRequestEventContext, PostRequestEventContext, PutRequestEventContext, RequestEventEmitter } from './makeRequest';

const mapUpdatedBanner = (updatedBannerId: number, responseData: Banner) =>
  (currentBanners: Banner[]): Banner[] =>
    currentBanners.map(banner => banner.id === updatedBannerId ? responseData : banner);

const createDeactivateRequestBody = (): DeactivateBannerRequest => ({
  endDate: moment().endOf('day').toISOString(),
});

export type BannersStore = {
  data: Banner[];
  fetchAll(): Promise<void>;
  // fetchStatus holds the status of the fetchAll action.
  fetchStatus: AsyncStatus;
  create(banner: CreateBannerRequest): Promise<void>;
  update(id: number, banner: UpdateBannerRequest): Promise<void>;
  deactivate(id: number): Promise<void>;
  // createUpdateStatus holds the status of create, update, and deactivate actions.
  createUpdateStatus: AsyncStatus;
  remove(id: number): Promise<void>;
  removeStatus: AsyncStatus;
  // clearStatusErrors clears the errors in fetchStatus, createUpdateStatus, and removeStatus.
  clearStatusErrors(): void;
  // pendingRequestBannerIds holds the IDs of banners for which there is a pending request to update, deactivate, or
  // delete the banner.
  pendingRequestBannerIds: number[];
  eventEmitter: RequestEventEmitter<BannersRequestContext>;
};

export enum PutOperation {
  Update,
  Deactivate,
}

export type BannersRequestContext =
  | GetRequestEventContext<GetBannersResponse>
  | PostRequestEventContext<Banner>
  | PutRequestEventContext<Banner> & { operation: PutOperation }
  | DeleteRequestEventContext<Pick<Banner, 'id'>>;

/**
 * A store of Banners.
 *
 * This hook does not have any mount or un-mount effects.
 */
const useBanners = (): BannersStore => {
  const [data, setData] = useState<Banner[]>([]);
  const { status: fetchStatus, ...fetchStatusFns } = useAsyncStatus();
  const { status: createUpdateStatus, ...createUpdateStatusFns } = useAsyncStatus();
  const { status: removeStatus, ...removeStatusFns } = useAsyncStatus();
  const [pendingRequestBannerIds, setPendingRequestBannerIds] = useState<number[]>([]);
  const eventEmitter = useRequestEventEmitter<BannersRequestContext>();

  // Synchronize the pendingRequestBannerIds array with the state of a request on the specified banner ID.
  const syncPendingRequestBannerId = (bannerId: number, state: RequestState) => {
    if (state === RequestState.Pending) {
      // Add an ID even if it is already in the array because there can be multiple pending requests for a banner.
      setPendingRequestBannerIds(
        (currentPendingRequestBannerIds: number[]): number[] =>
          [...currentPendingRequestBannerIds, bannerId],
      );
    } else {
      // Filter out only one instance of an ID because there can be multiple pending requests for a banner.
      setPendingRequestBannerIds(
        (currentPendingRequestBannerIds: number[]): number[] =>
          dropAt(currentPendingRequestBannerIds, currentPendingRequestBannerIds.indexOf(bannerId)),
      );
    }
  };

  const clearStatusErrors = () => {
    fetchStatusFns.clearError();
    createUpdateStatusFns.clearError();
    removeStatusFns.clearError();
  };

  const fetchAll = () =>
    makeRequest<GetBannersResponse, BannersRequestContext>({
      req: () => get<GetBannersResponse>('/api/banners'),
      setData: responseData => setData(responseData || []),
      getEventContext: responseData => ({ method: Method.GET, data: responseData }),
      statusFns: fetchStatusFns,
      eventEmitter,
    });

  const create = async (banner: CreateBannerRequest) =>
    makeRequest<Banner, BannersRequestContext>({
      req: () => post<CreateBannerRequest, Banner>('/api/banners', banner),
      setData: responseData => setData(currentData => [...currentData, responseData as Banner]),
      getEventContext: responseData => ({ method: Method.POST, data: responseData as Banner }),
      statusFns: createUpdateStatusFns,
      eventEmitter,
    });

  const update = async (id: number, banner: UpdateBannerRequest) =>
    makeRequest<Banner, BannersRequestContext>({
      req: () => put<UpdateBannerRequest, Banner>(`/api/banners/${id}`, banner),
      setData: responseData => responseData && setData(mapUpdatedBanner(id, responseData)),
      getEventContext: (responseData, state) => {
        syncPendingRequestBannerId(id, state);
        return { method: Method.PUT, data: responseData, operation: PutOperation.Update };
      },
      statusFns: createUpdateStatusFns,
      eventEmitter,
    });

  const deactivate = async (id: number) =>
    makeRequest<Banner, BannersRequestContext>({
      req: () => put<DeactivateBannerRequest, Banner>(`/api/banners/${id}/deactivate`, createDeactivateRequestBody()),
      setData: responseData => responseData && setData(mapUpdatedBanner(id, responseData)),
      getEventContext: (responseData, state) => {
        syncPendingRequestBannerId(id, state);
        return { method: Method.PUT, data: responseData, operation: PutOperation.Deactivate };
      },
      statusFns: createUpdateStatusFns,
      eventEmitter,
    });

  const remove = async (id: number) =>
    makeRequest<undefined, BannersRequestContext>({
      req: () => rm<undefined>(`/api/banners/${id}`),
      setData: () => setData(currentData => currentData.filter(banner => banner.id !== id)),
      getEventContext: (_responseData, state) => {
        syncPendingRequestBannerId(id, state);
        return { method: Method.DELETE };
      },
      statusFns: removeStatusFns,
      eventEmitter,
    });

  return {
    data,
    fetchAll,
    fetchStatus,
    create,
    update,
    deactivate,
    createUpdateStatus,
    remove,
    removeStatus,
    clearStatusErrors,
    pendingRequestBannerIds,
    eventEmitter,
  };
};

export default useBanners;
